/**
 * Related entities — entities identification found connected to a candidate
 * (officers of a screened company, companies an individual directs).
 *
 * Once the analyst picks the right candidate, the losing candidates' entities
 * are superseded and the winner's become adjudicable in the triage
 * Identification step: research one to enrich the subject, keep it as context,
 * or drop it. Non-blocking — anything left pending becomes context at Start.
 * Admin-only.
 */

export type RelatedEntityKind =
  | 'director'
  | 'officer'
  | 'shareholder'
  | 'beneficial_owner'
  | 'employer'
  | 'parent'
  | 'subsidiary'
  | 'education'
  | 'associate';

export type RelatedEntityDisposition =
  | 'pending'
  | 'researched'
  | 'context_only'
  | 'dismissed'
  | 'superseded';

export type RelatedEntityDiscoveredBy = 'identification' | 'research';

export type RelatedEntityConfidence = 'low' | 'medium' | 'high';

/** The three decisions an analyst can make about an entity. */
export type RelatedEntityAction = 'research' | 'context_only' | 'dismiss';

export interface RelatedEntity {
  id: string;
  subjectId: string;
  name: string;
  entityType: string | null;
  relationshipKind: RelatedEntityKind;
  relationship: string | null;
  relationshipDetail: string | null;
  attributes: Record<string, unknown>;
  sources: Array<Record<string, unknown>>;
  confidence: RelatedEntityConfidence;
  discoveredBy: RelatedEntityDiscoveredBy;
  disposition: RelatedEntityDisposition;
  depth: number;
  surfaced: boolean;
  parentRelatedEntityId: string | null;
  /** Present once researched — shown inline so the analyst sees the outcome. */
  researchSummary: string | null;
  decidedByUserId: string | null;
  decidedAt: string | null;
}

/** Entities grouped by the subject they were discovered from. */
export interface RelatedEntityGroup {
  subjectId: string;
  subjectName: string;
  entities: RelatedEntity[];
  /**
   * Depth-2 entities found while researching one of the above. Kept apart so
   * they don't compete for attention until explicitly opted into.
   */
  undisclosedDeeper: RelatedEntity[];
}

export interface RelatedEntitiesResult {
  reportId: string;
  groups: RelatedEntityGroup[];
  pendingCount: number;
  researchInFlight: boolean;
}

export interface RelatedEntityDispositionResult {
  entity: RelatedEntity;
  /**
   * Whether deep research is still running. Nothing is gated on adjudication
   * itself, but Start is blocked while research is in flight, so the UI can
   * explain why.
   */
  researchInFlight: boolean;
}

// --- Wire types (snake_case) ------------------------------------------------

export interface ApiRelatedEntity {
  id: string;
  subject_id: string;
  name: string;
  entity_type: string | null;
  relationship_kind: RelatedEntityKind;
  relationship: string | null;
  relationship_detail: string | null;
  attributes: Record<string, unknown>;
  sources: Array<Record<string, unknown>>;
  confidence: RelatedEntityConfidence;
  discovered_by: RelatedEntityDiscoveredBy;
  disposition: RelatedEntityDisposition;
  depth: number;
  surfaced: boolean;
  parent_related_entity_id: string | null;
  research_summary: string | null;
  decided_by_user_id: string | null;
  decided_at: string | null;
}

export interface ApiRelatedEntityGroup {
  subject_id: string;
  subject_name: string;
  entities: ApiRelatedEntity[];
  undisclosed_deeper: ApiRelatedEntity[];
}

export interface ApiRelatedEntitiesResponse {
  report_id: string;
  groups: ApiRelatedEntityGroup[];
  pending_count: number;
  research_in_flight: boolean;
}

export interface ApiRelatedEntityDispositionResponse {
  entity: ApiRelatedEntity;
  research_in_flight: boolean;
}

export function transformToRelatedEntity(api: ApiRelatedEntity): RelatedEntity {
  return {
    id: api.id,
    subjectId: api.subject_id,
    name: api.name,
    entityType: api.entity_type,
    relationshipKind: api.relationship_kind,
    relationship: api.relationship,
    relationshipDetail: api.relationship_detail,
    attributes: api.attributes ?? {},
    sources: api.sources ?? [],
    confidence: api.confidence,
    discoveredBy: api.discovered_by,
    disposition: api.disposition,
    depth: api.depth,
    surfaced: api.surfaced,
    parentRelatedEntityId: api.parent_related_entity_id,
    researchSummary: api.research_summary,
    decidedByUserId: api.decided_by_user_id,
    decidedAt: api.decided_at,
  };
}

export function transformToRelatedEntityGroup(api: ApiRelatedEntityGroup): RelatedEntityGroup {
  return {
    subjectId: api.subject_id,
    subjectName: api.subject_name,
    entities: (api.entities ?? []).map(transformToRelatedEntity),
    undisclosedDeeper: (api.undisclosed_deeper ?? []).map(transformToRelatedEntity),
  };
}

export function transformToRelatedEntities(api: ApiRelatedEntitiesResponse): RelatedEntitiesResult {
  return {
    reportId: api.report_id,
    groups: (api.groups ?? []).map(transformToRelatedEntityGroup),
    pendingCount: api.pending_count,
    researchInFlight: api.research_in_flight,
  };
}

export function transformToRelatedEntityDisposition(
  api: ApiRelatedEntityDispositionResponse
): RelatedEntityDispositionResult {
  return {
    entity: transformToRelatedEntity(api.entity),
    researchInFlight: api.research_in_flight,
  };
}

/**
 * Human labels for the relationship vocabulary.
 *
 * The enum is chosen for what data acquisition does with it, not for display,
 * so `beneficial_owner` and the rest need rendering.
 */
export const RELATIONSHIP_KIND_LABELS: Record<RelatedEntityKind, string> = {
  director: 'Director',
  officer: 'Officer',
  shareholder: 'Shareholder',
  beneficial_owner: 'Beneficial owner',
  employer: 'Employer',
  parent: 'Parent company',
  subsidiary: 'Subsidiary',
  education: 'Education',
  associate: 'Associate',
};
